import { EventType, SleepStage } from '@/common/types';

// Respiratory events on Nasal_pressure use the bare base name (no channel
// suffix), while all other channels (e.g. Resp_flow) append the signal name.
const respiratoryEventType = (base: EventType, signalName: string): string =>
  signalName === 'Nasal_pressure' ? base : `${base} ${signalName}`;

// Arousal events are channel-specific: resolve the exact EventType from the
// active signal so insertEventByType receives a precise value.
const arousalEventType = (signalName: string): EventType => {
  if (signalName === 'EEG_frontal_R') return EventType.AROUSAL_FRONTAL_R;
  return EventType.AROUSAL_FRONTAL_L;
};

export const keyboardShortcutMapping = (d3Client: any, signalName: string) => ({
  c: () => d3Client.insertEventByType(respiratoryEventType(EventType.APNEA_CENTRAL, signalName)),
  m: () => d3Client.insertEventByType(respiratoryEventType(EventType.APNEA_MIXED, signalName)),
  o: () =>
    d3Client.insertEventByType(respiratoryEventType(EventType.APNEA_OBSTRUCTIVE, signalName)),
  h: () =>
    d3Client.insertEventByType(respiratoryEventType(EventType.HYPOPNEA_OBSTRUCTIVE, signalName)),
  g: () => d3Client.insertEventByType(respiratoryEventType(EventType.HYPOPNEA_CENTRAL, signalName)),
  z: () => d3Client.insertEventByType(EventType.CHEYNE_STOKES_BREATHING),
  a: () => d3Client.insertEventByType(arousalEventType(signalName)),
  l: () => d3Client.insertEventByType(EventType.LEG_MOVEMENT),
  p: () => d3Client.insertEventByType(EventType.PERIODIC_LEG_MOVEMENT),
  d: () => d3Client.insertEventByType(EventType.DESATURATION),
  u: () => d3Client.insertEventByType(respiratoryEventType(EventType.RERA, signalName)),
  t: () => d3Client.insertEventByType(EventType.TACHYCARDIA),
  b: () => d3Client.insertEventByType(EventType.BRADYCARDIA),
  s: () => d3Client.insertEventByType(EventType.SNORING),
  x: () => d3Client.insertEventByType(`Artifact ${signalName}`),

  '1': () => d3Client.updateEventByType(SleepStage.N1),
  '2': () => d3Client.updateEventByType(SleepStage.N2),
  '3': () => d3Client.updateEventByType(SleepStage.N3),
  '5': () => d3Client.updateEventByType(SleepStage.REM),
  '0': () => d3Client.updateEventByType(SleepStage.Wake),
  ' ': () => d3Client.updateEventByType(SleepStage.Unstaged),

  Delete: () => d3Client.deleteHoveredEvent(),
  Backspace: () => d3Client.deleteHoveredEvent()
});

export const keyboardZoomMapping = (chartContextMenu: any) => ({
  '+': () => chartContextMenu.plusZoom?.(),
  '-': () => chartContextMenu.minusZoom?.(),
  '*': () => chartContextMenu.fitToScreenZoom?.(),
  f: () => chartContextMenu.fitAllToPane?.(),
  r: () => chartContextMenu?.resetHoveredToDefault?.(),
  o: () => chartContextMenu?.resetAllToDefault?.(),
  ArrowUp: () => chartContextMenu.upZoom?.(),
  ArrowDown: () => chartContextMenu.downZoom?.()
});

export const sleepStageKeyMap: Partial<Record<string, string>> = {
  '0': SleepStage.Wake,
  '1': SleepStage.N1,
  '2': SleepStage.N2,
  '3': SleepStage.N3,
  '5': SleepStage.REM
};

export const eventTypeShortcutMap: Record<string, string> = {
  [EventType.APNEA_CENTRAL]: 'c',
  [EventType.APNEA_CENTRAL_RESP_FLOW]: 'c',
  [EventType.APNEA_MIXED]: 'm',
  [EventType.APNEA_MIXED_RESP_FLOW]: 'm',
  [EventType.APNEA_OBSTRUCTIVE]: 'o',
  [EventType.APNEA_OBSTRUCTIVE_RESP_FLOW]: 'o',
  [EventType.HYPOPNEA_OBSTRUCTIVE]: 'h',
  [EventType.HYPOPNEA_OBSTRUCTIVE_RESP_FLOW]: 'h',
  [EventType.HYPOPNEA_CENTRAL]: 'g',
  [EventType.HYPOPNEA_CENTRAL_RESP_FLOW]: 'g',
  [EventType.CHEYNE_STOKES_BREATHING]: 'z',
  [EventType.AROUSAL_FRONTAL_L]: 'a',
  [EventType.AROUSAL_FRONTAL_R]: 'a',
  [EventType.LEG_MOVEMENT]: 'l',
  [EventType.PERIODIC_LEG_MOVEMENT]: 'p',
  [EventType.DESATURATION]: 'd',
  [EventType.RERA]: 'u',
  [EventType.RERA_RESP_FLOW]: 'u',
  [EventType.TACHYCARDIA]: 't',
  [EventType.BRADYCARDIA]: 'b',
  [EventType.SNORING]: 's',
  [SleepStage.N1]: '1',
  [SleepStage.N2]: '2',
  [SleepStage.N3]: '3',
  [SleepStage.REM]: '5',
  [SleepStage.Wake]: '0',
  [SleepStage.Unstaged]: ' '
};
